import React, { AnchorHTMLAttributes, FC, ReactNode, useEffect, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import { Mention, OnChangeHandlerFunc } from 'react-mentions'
import rehypeRaw from 'rehype-raw'

import { MENTION_PATTERN, USER_MENTION_PATTERN, UUID_PATTERN } from '../../utils'
import { Avatar, getAvantarColor, getAvatarInitials } from '../Avatar'
import * as S from './MentionUser.styles'
import { MentionUserProps, SugestionProps } from './MentionUser.types'
import { applyPlainTextEdit } from './utils/applyPlainTextEdit/applyPlainTextEdit'

export const MentionUser: FC<MentionUserProps> = ({
  value,
  onChange,
  handleOnAdd,
  sugestions,
  readyOnly,
  placeholder,
}) => {
  const isEditable = !readyOnly && Boolean(sugestions)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const replacedPlainTextRef = useRef<string | null>(null)

  useEffect(() => {
    const textarea = inputRef.current
    if (!textarea) return

    const trackReplacement = (event: Event) => {
      const isReplacement =
        event instanceof InputEvent && event.inputType === 'insertReplacementText'
      replacedPlainTextRef.current = isReplacement ? textarea.value : null
    }

    textarea.addEventListener('input', trackReplacement)
    return () => textarea.removeEventListener('input', trackReplacement)
  }, [isEditable])

  // react-mentions locates an edit by the caret it last saw, so a spell-check replacement
  // elsewhere in the text duplicates or drops content; rebuild it from the textarea instead.
  const handleChange: OnChangeHandlerFunc = (event, newValue, newPlainTextValue, mentions) => {
    const replacedPlainText = replacedPlainTextRef.current
    replacedPlainTextRef.current = null

    if (replacedPlainText == null) {
      onChange?.(event, newValue, newPlainTextValue, mentions)
      return
    }

    const edit = applyPlainTextEdit({ value: value ?? '', plainText: replacedPlainText })
    onChange?.({ target: { value: edit.value } }, edit.value, edit.plainText, edit.mentions)
  }

  // This is necessary to indetify mentions (@[userName](userId)) on ready only mode and replace the node style
  const parsedValue =
    readyOnly && value
      ? value.replace(/\n/gi, '<br>').replace(USER_MENTION_PATTERN, (text: string) => {
          return text.replace('@', '')
        })
      : ''

  const renderSuggestionOption = (
    suggestion: SugestionProps,
    _search: string,
    _highlightedDisplay: ReactNode,
    _index: number,
    focused: boolean,
  ) => {
    return (
      <S.SuggestionContainer key={suggestion.id} focused={focused}>
        <Avatar
          noBorder
          bgColor={getAvantarColor(suggestion.display)}
          initials={getAvatarInitials(suggestion.display)}
          widthValue="26px"
          heightValue="26px"
        />
        <S.Name>{suggestion.display}</S.Name>
      </S.SuggestionContainer>
    )
  }

  return isEditable ? (
    <S.StyledMetionsInput
      inputRef={inputRef}
      value={value}
      onChange={handleChange}
      style={S.mentionsInputStyles}
      placeholder={placeholder}
    >
      <Mention
        trigger={MENTION_PATTERN}
        data={sugestions}
        appendSpaceOnAdd
        onAdd={handleOnAdd}
        renderSuggestion={renderSuggestionOption}
        style={S.mentionStyles}
      />
    </S.StyledMetionsInput>
  ) : parsedValue ? (
    <S.CommentTextWrapper>
      <ReactMarkdown
        className="mention-user-readonly"
        children={parsedValue}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: (props: AnchorHTMLAttributes<HTMLAnchorElement>) => {
            const { href, children } = props
            const isUser = href ? UUID_PATTERN.test(href) : false
            const hasHttp = href?.startsWith('http')
            return isUser ? (
              <S.StyledMentionText>{children}</S.StyledMentionText>
            ) : (
              <S.Link
                href={hasHttp ? href : `http://${href}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                {children}
              </S.Link>
            )
          },
        }}
      />
    </S.CommentTextWrapper>
  ) : null
}
